<template lang="html">
  <section class="section">
    <div class="container">
      <div class="columns">
        <!-- Left menu -->
        <div class="column is-one-fifth">
          <!-- Profile when online-->
          <div v-if="currentUser" class="box has-text-centered">
            <router-link :to="{ path: `/users/${currentUser.attributes.username}` }">
              <h1 class="title is-size-5 has-text-dark-orange">
                {{ currentUser.attributes.cosplayer_name }}
              </h1>
            </router-link>
            <div class="has-text-centered">
              <router-link :to="{ path: `/users/${currentUser.attributes.username}` }">
                <figure class="image is-96x96 is-inline-block">
                  <img
                    style="border-radius: 50%"
                    :src="currentUser.attributes.avatar.url"
                  />
                </figure>
              </router-link>
            </div>
            <small class="is-italic">"{{ currentUser.attributes.motto }}"</small><br />
            <router-link :to="{ path: `/users/${currentUser.attributes.username}` }"
              ><small class="has-text-gray">@{{ currentUser.attributes.username }}</small
              ><br
            /></router-link>
            <country-flag :country="currentUser.attributes.country" :rounded="true" />
          </div>

          <!-- Profile when offline-->
          <div v-else class="box has-text-left">
            <h1 class="title is-size-5 has-text-dark-orange">Log in</h1>
            <form action="">
              <ValidationObserver ref="observer" v-slot="{ passes }">
                <section class="">
                  <b-notification
                    @close="loginErrors = null"
                    :active="loginErrors != null"
                    type="is-danger"
                    has-icon
                    aria-close-label="Close notification"
                    role="alert"
                  >
                    {{ loginErrors }}
                  </b-notification>

                  <ValidationProvider
                    rules="required"
                    name="Login"
                    v-slot="{ errors, valid }"
                  >
                    <b-field
                      label="Username or email address"
                      :type="{ 'is-danger': errors[0] }"
                      :message="errors"
                    >
                      <b-input
                        type="text"
                        v-model="session.login"
                        placeholder="Your email or username"
                        required
                      >
                      </b-input>
                    </b-field>
                  </ValidationProvider>

                  <ValidationProvider
                    rules="required"
                    name="Password"
                    v-slot="{ errors, valid }"
                  >
                    <b-field
                      label="Password"
                      :type="{ 'is-danger': errors[0] }"
                      :message="errors"
                    >
                      <b-input
                        type="password"
                        v-model="session.password"
                        password-reveal
                        placeholder="Your password"
                        required
                      >
                      </b-input>
                    </b-field>
                  </ValidationProvider>

                  <small>
                    <router-link :to="{ path: '/recover' }"
                      >Forgot your password?</router-link
                    ><br />
                    <router-link :to="{ path: '/register' }"
                      >I don't have an account yet.</router-link
                    >
                  </small>
                </section>
                <section class="buttons py-2">
                  <b-button
                    type="is-primary"
                    tag="input"
                    native-type="submit"
                    @click.prevent="passes(login)"
                    @keyup.enter="passes(login)"
                    value="Login"
                  />
                  <b-button
                    class="button"
                    type="button"
                    @click="$router.push('/register')"
                    >Register</b-button
                  >
                </section>
              </ValidationObserver>
            </form>
          </div>

          <!-- Last crafts -->
          <div v-if="currentUser" class="box">
            <h1 class="title has-text-dark-orange is-size-5">Last crafts</h1>
            <div class="columns is-multiline">
              <!-- Craft 1 -->
              <div class="column is-full">
                <div class="content">
                  <strong class="ellipsis2">Coming soon </strong>
                  <small class="ellipsis2">Coming soon</small>
                </div>
                <figure class="image content">
                  <img
                    src="https://costuto-public.s3.eu-central-1.amazonaws.com/images/placeholder_3x2.png"
                    alt=""
                  />
                </figure>
                <div
                  style="border-bottom: 1px solid rgba(150, 150, 150, 0.5)"
                  class="columns is-vcentered"
                >
                  <div class="column">
                    <b-progress type="is-primary is-small" :value="30"></b-progress>
                  </div>
                  <div class="column is-narrow">
                    <a
                      ><b-icon
                        type="is-success"
                        size=""
                        icon="arrow-right-bold-circle-outline"
                      ></b-icon
                    ></a>
                  </div>
                </div>
              </div>

              <!-- Craft 2 -->
              <div class="column is-full">
                <div class="content">
                  <strong class="ellipsis2">Coming soon</strong>
                  <small class="ellipsis2">Coming soon</small>
                </div>
                <figure class="image content">
                  <img
                    src="https://costuto-public.s3.eu-central-1.amazonaws.com/images/placeholder_3x2.png"
                    alt=""
                  />
                </figure>
                <div class="columns is-vcentered">
                  <div class="column">
                    <b-progress type="is-primary is-small" :value="30"></b-progress>
                  </div>
                  <div class="column is-narrow">
                    <a
                      ><b-icon
                        type="is-success"
                        size=""
                        icon="arrow-right-bold-circle-outline"
                      ></b-icon
                    ></a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Newsfeed -->
        <div class="column">
          <b-tabs type="is-toggle" expanded>
            <!-- "All" feed -->
            <b-tab-item>
              <template slot="header">
                <b-icon icon="infinity"></b-icon>
                <span class="has-text-weight-semibold">All</span>
              </template>
              <div class="columns is-multiline is-centered">
                <publications :category="'all'" />
              </div>
            </b-tab-item>

            <!-- "Popular" feed -->
            <b-tab-item disabled>
              <template slot="header">
                <b-icon icon="star-four-points-outline"></b-icon>
                <span class="has-text-weight-semibold"> Popular </span>
              </template>
              <div class="columns is-multiline is-centered">
                <publications :category="'popular'" />
              </div>
            </b-tab-item>

            <!-- "Teachers" feed -->
            <b-tab-item disabled>
              <template slot="header">
                <b-icon icon="teach"></b-icon>
                <span class="has-text-weight-semibold"> Teachers </span>
              </template>
              <div class="columns is-multiline is-centered">
                <publications :category="'teacher'" />
              </div>
            </b-tab-item>

            <!-- "Similar" feed -->

            <b-tab-item disabled>
              <template slot="header">
                <b-icon icon="account-switch"></b-icon>
                <span class="has-text-weight-semibold"> Similar </span>
              </template>

              <div class="columns is-multiline is-centered">
                <!-- <news-element/> -->
              </div>
            </b-tab-item>
          </b-tabs>
        </div>

        <!-- Right menu -->
        <div class="column is-one-fifth">
          <!-- Last joined -->
          <div class="box">
            <h1 class="has-text-dark-orange title is-size-5">Last joined</h1>
            <div class="columns">
              <div v-for="user in lastJoined" :key="user.id" class="column is-3">
                <router-link :to="{ path: `/users/${user.attributes.username}` }">
                  <b-tooltip
                    :label="`${user.attributes.cosplayer_name} - ${dateFromNow(
                      user.attributes.created_at
                    )}`"
                  >
                    <p class="image">
                      <img style="border-radius: 50%" :src="user.attributes.avatar.url" />
                    </p>
                  </b-tooltip>
                </router-link>
              </div>
            </div>
          </div>

          <!-- Suggestions -->
          <div class="box">
            <h1 class="has-text-dark-orange title is-size-5">Suggestions</h1>

            <div class="columns is-multiline">
              <div v-for="user in lastJoined" :key="user.id" class="column is-full">
                <router-link :to="{ path: `/users/${user.attributes.username}` }">
                  <div class="columns">
                    <div class="column is-4">
                      <b-tooltip :label="user.attributes.cosplayer_name">
                        <p class="image">
                          <img
                            style="border-radius: 50%"
                            :src="user.attributes.avatar.url"
                          />
                        </p>
                      </b-tooltip>
                    </div>
                    <div class="column has-text-dark">
                      <strong>{{ user.attributes.cosplayer_name }}</strong
                      ><br />
                      <small>@{{ user.attributes.username }}</small>
                    </div>
                  </div>
                </router-link>
              </div>
            </div>
          </div>

          <!-- Featured -->
          <!-- <div class="box">
            <h1 class="has-text-dark-orange title is-size-5">Featured</h1>
            <div class="columns">
              <div class="column">
                <div class="columns">
                  <div class="column">
                    <strong>Cosplay Shop</strong><br />
                    <small>@cosplayshop_be</small>
                    <b-rate :value="3" size="is-small"></b-rate>
                  </div>
                  <div class="column is-narrow">
                    <country-flag country="BE" :rounded="true" />
                  </div>
                </div>
                <figure class="image content">
                  <img
                    src="https://costuto-public.s3.eu-central-1.amazonaws.com/images/placeholder_3x2.png"
                    alt=""
                  />
                </figure>
              </div>
            </div>
          </div> -->

          <!-- Footer -->
          <div class="box">
            <figure class="image">
              <img
                src="https://costuto-public.s3.eu-central-1.amazonaws.com/images/tanukos.png"
              />
            </figure>
            <small><strong>Costuto</strong> ©2020</small><br />
            <small>All Rights Reserved</small><br />
            <a href="https://twitter.com/Tanukos/"
              ><b-icon icon="twitter" size="is-small"></b-icon
            ></a>
            <a href="https://www.youtube.com/channel/UCe6eo_Hj5q4Efs8vDdWXR5A/"
              ><b-icon icon="youtube" size="is-small"></b-icon
            ></a>
            <a href="https://www.facebook.com/costuto/"
              ><b-icon icon="facebook" size="is-small"></b-icon
            ></a>
            <a href="https://www.instagram.com/cos.tuto/"
              ><b-icon icon="instagram" size="is-small"></b-icon
            ></a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Publications from './Publications.vue'
import CountryFlag from 'vue-country-flag'
import { dateMixin } from '@/mixins/dateMixin'
import { mapGetters } from 'vuex'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
export default {
  mixins: [dateMixin],

  data () {
    return {
      lastJoined: null,
      loginErrors: null,
      session: {
        login: null,
        password: null
      }
    }
  },
  computed: {
    // mix the getters into computed with object spread operator
    ...mapGetters(['currentUser'])
  },
  components: {
    CountryFlag,
    Publications,
    ValidationObserver,
    ValidationProvider
  },
  created () {
    this.fetchLastJoined()
  },
  methods: {
    fetchLastJoined () {
      this.$store
        .dispatch('search/users/create', { order: { created_at: 'desc' }, limit: 4 })
        .then((resp) => {
          this.lastJoined = resp.data.data
        })
        .catch((error) => {
          console.log('Errors')
          console.log(error.response)
        })
    },
    login () {
      this.$store
        .dispatch('sessions/create', this.session)
        .then((cosplayer) => {
          this.close()
          this.$router.push('/').catch((_) => {})
          this.$buefy.toast.open({
            message: `Welcome back, ${cosplayer} ヽ(*⌒▽⌒*)ﾉ !`,
            type: 'is-success'
          })
        })
        .catch((error) => {
          this.loginErrors = this.$t(`login.${error.response.data.errors}`)
        })
    },
    close () {
      this.$parent.close()
    }
  }
}
</script>

<style lang="scss" scoped></style>
